import React, { useEffect, useCallback, useState } from "react";
import styled from "styled-components";
import { FormikProps, withFormik } from "formik";
import * as Yup from "yup";
import {
  Input,
  RadioGroup,
  RadioOption,
  DropdownOptionProps,
  DropdownOption,
  Dropdown,
  Link,
  Icon
} from "@abb/abb-common-ux-react";
import { DatePickerInput } from "../../../../framework/components/DatePickerInput";
import {
  handleFormValueChange,
  isFormFieldValid,
  parseDateStringForYupValidation,
  handlePressEnter
} from "../../../../utilities/formikUtils";
import { HorizontalElementContainer } from "../../../../framework/components/HorizontalElementContainer";
import { orderSearchFormDefaultFields } from "../reducers/ordersViewReducer";
import AuthorizedComponent from "../../../../framework/components/AuthorizedComponent";
import { InputLabel } from "framework/components/InputLabel";
import { SearchFormInfo } from "../../../../framework/components/SearchFormInfo";
import { OrderSearchStatus } from "../../../../api/models/OrderSearchStatus";
import { AllCompaniesSelectionIdentifier } from "../../deliveriesHome/reducers/deliveriesHomeViewReducer";
import { GridApi } from "ag-grid-community";
import { WildcardPopup } from "./WildcardPopup";
import { Button } from "framework/components/Button";
import { useTranslation } from "react-i18next";
import { NavMenuCompany } from "shared/menubar/components/NavItem";
import { AccessRights } from "api/models/AccessRights";
import { roleConstants } from "constants/roleConstants";

export const getOrderSearchTooltips = (t: (text: string) => string) => {
  return {
    orderOrReference: t(
      "Order / reference field searches from fields: purchase order, order acknowledgement, PST reference and end customer purchase order"
    ),
    orderingCode: t(
      "Ordering code searches from fields: Vendor ordering code and Customer ordering code"
    ),
    trackingNumber: t("Tracking number searches from fields: Shipping reference and AWB number"),
    etcdICVCode: t("Search from company's ETCD ICV code")
  };
};

export interface OrderSearchFormProps {
  plants: DropdownOptionProps[] | undefined;
  countries: DropdownOptionProps[] | undefined;
  selectedCompany: NavMenuCompany | undefined;
  storedOrderFields: OrderSearchFields;
  onSubmit: (fields: OrderSearchFields, companyId?: string | null) => void;
  gridApi: GridApi | undefined;
}

export interface OrderSearchFields {
  order: string | undefined;
  orderingCode: string | undefined;
  trackingNumber: string | undefined;
  status: OrderSearchStatus;
  dateFrom: string | undefined;
  dateTo: string | undefined;
  icvCode: string | undefined;
  countryCode: string | undefined;
  customerCode: string | undefined;
  limitedSearch: boolean | undefined;
  plantId: string | undefined;
}

const CustomRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const FormIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;

const IconText = styled.div`
  margin-left: ${(props) => props.theme.sizes.sm};
  font-size: ${(props) => props.theme.fonts.sizes.fontSizeS};
`;

const SwitchSearchType = styled(Link)`
  font-size: ${(props) => props.theme.fonts.sizes.fontSizeS};
  cursor: pointer;
`;

const CenteredIcon = styled(Icon)`
  align-self: center;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.sizes.lm};
  padding: ${(props) => props.theme.sizes.lm};
`;

const OrderSearchSchema = Yup.object().shape({
  dateFrom: Yup.date()
    .transform(parseDateStringForYupValidation)
    .optional()
    .typeError("Must be a valid date"),
  dateTo: Yup.date()
    .transform(parseDateStringForYupValidation)
    .optional()
    .typeError("Must be a valid date")
});

const InnerSearchForm: React.FunctionComponent<
  OrderSearchFormProps & FormikProps<OrderSearchFields>
> = (props) => {
  const selectedCompany = props.selectedCompany;
  const onSubmit = props.submitForm;
  const changleFieldValue = props.handleChange;
  const setFieldTouched = props.setFieldTouched;
  const setFieldValue = props.setFieldValue;

  const [valuesInitied, setValuesInited] = useState<boolean>(false);
  const gridApi = props.gridApi;

  const handleValueChange = useCallback(
    (key: string, value: string) => {
      changleFieldValue(key)(value);
      setFieldTouched(key);
    },
    [changleFieldValue, setFieldTouched]
  );

  const { t } = useTranslation();

  // Submit if company selection changes.
  useEffect(() => {
    if (valuesInitied) {
      if (selectedCompany?.value) {
        handleValueChange("customerCode", "");
        setFieldValue("country", undefined);
      }
      onSubmit();
    }
  }, [onSubmit, selectedCompany, handleValueChange, gridApi, setFieldValue, valuesInitied]);

  const { setValues, storedOrderFields } = props;

  useEffect(() => {
    setValues(storedOrderFields);
    setValuesInited(true);
  }, [storedOrderFields, setValues]);

  const isLimitedSearch = props.storedOrderFields.limitedSearch;

  const drawSearchModeSwitch = () => {
    return (
      <AuthorizedComponent
        accessRights={[AccessRights.ViewCompanyOrders, AccessRights.ViewAllOrders]}
      >
        <div>
          <FormIconContainer>
            <CenteredIcon name="abb/information-circle-1" />
            <IconText>{t("Limited search")}</IconText>
          </FormIconContainer>
        </div>
        <div>
          <SwitchSearchType
            onClick={() => {
              setFieldValue("limitedSearch", false);
              props.submitForm();
            }}
          >
            {t("Switch to normal search")}
          </SwitchSearchType>
        </div>
      </AuthorizedComponent>
    );
  };

  const orderSearchTooltips = getOrderSearchTooltips(t);

  const drawNormalSearchFields = () => {
    return (
      <>
        <AuthorizedComponent
          accessRights={[AccessRights.ViewCompanyOrders, AccessRights.ViewAllOrders]}
        >
          <div>
            <InputLabel
              icon="abb/information-circle-1"
              label={t("Ordering code")}
              tooltipText={orderSearchTooltips.orderingCode}
            />

            <Input
              type="normal"
              showClearIcon
              value={props.values.orderingCode}
              onValueChange={(e: string) => {
                handleFormValueChange(props, "orderingCode", e);
              }}
              onKeyUp={(event) => handlePressEnter(event, onSubmit)}
              dataType="text"
            />
          </div>
          <div>
            <InputLabel
              icon="abb/information-circle-1"
              label={t("Tracking number")}
              tooltipText={orderSearchTooltips.trackingNumber}
            />
            <Input
              id="trackingNumber"
              type="normal"
              showClearIcon
              value={props.values.trackingNumber}
              onValueChange={(e: string) => {
                handleFormValueChange(props, "trackingNumber", e);
              }}
              onKeyUp={(event) => handlePressEnter(event, onSubmit)}
              dataType="text"
            />
          </div>
          <div>
            {
              // Copied the styled from label of input element as there is no "label" props in Radiogroup
            }
            <span className="ABB_CommonUX_Input__root ABB_CommonUX_Input__light ABB_CommonUX_Input__normal ABB_CommonUX_Input__isValid">
              <span className="ABB_CommonUX_Input__label">{t("Order status")}</span>
            </span>
            <CustomRadioGroup
              monochrome={true}
              value={props.values.status.toString()}
              orientation="horizontal"
              onChange={(newState: string) => {
                handleFormValueChange(props, "status", newState);
              }}
            >
              <RadioOption value={OrderSearchStatus.All} text={t("All")} />
              <RadioOption value={OrderSearchStatus.Open} text={t("Open")} />
              <RadioOption value={OrderSearchStatus.Dispatched} text={t("Dispatched")} />
            </CustomRadioGroup>
          </div>

          <div>
            <DatePickerInput
              label={t("Datetime from")}
              value={props.values.dateFrom}
              type="normal"
              showClearIcon
              showValidationIconWhenInvalid
              validator={() => {
                return isFormFieldValid(props, "dateFrom");
              }}
              onValueChange={(newValue: string) => {
                handleFormValueChange(props, "dateFrom", newValue);
              }}
            />
          </div>
          <div>
            <DatePickerInput
              label={t("Datetime to")}
              type="normal"
              value={props.values.dateTo}
              showClearIcon
              showValidationIconWhenInvalid
              validator={() => {
                return isFormFieldValid(props, "dateTo");
              }}
              onValueChange={(newValue: string) => {
                handleFormValueChange(props, "dateTo", newValue);
              }}
            />
          </div>
        </AuthorizedComponent>
        <AuthorizedComponent
          accessRights={[AccessRights.SystemAdmin, AccessRights.ManageDeliveriesCompanies]}
        >
          <div>
            <Input
              type="normal"
              dataType="text"
              showClearIcon
              label={t("ETCD ICV code")}
              value={props.values.icvCode}
              onValueChange={(e: string) => {
                handleFormValueChange(props, "icvCode", e);
              }}
              onKeyUp={(event) => handlePressEnter(event, onSubmit)}
            />
          </div>
          <div>
            <Input
              type="normal"
              dataType="text"
              showClearIcon
              label={t("Customer code")}
              disabled={selectedCompany?.value === AllCompaniesSelectionIdentifier ? false : true}
              value={props.values.customerCode}
              onValueChange={(e: string) => {
                handleFormValueChange(props, "customerCode", e);
              }}
              onKeyUp={(event) => handlePressEnter(event, onSubmit)}
            />
          </div>
        </AuthorizedComponent>
        <AuthorizedComponent
          accessRights={[AccessRights.AdvancedOrderTracking]}
          companyId={roleConstants.GlobalRoleIdentifier}
        >
          <div>
            <Dropdown
              monochrome
              label={t("Plant")}
              clearable
              searchable
              onChange={(
                selection: Array<{
                  value: any;
                  label: string;
                  isNew: boolean;
                }>
              ) => {
                handleFormValueChange(
                  props,
                  "plantId",
                  selection.length > 0 ? selection[0].value : ""
                );
              }}
              value={[
                {
                  value: props.values.plantId,
                  label: props.plants?.find((x) => x.value === props.values.plantId)?.label
                }
              ]}
            >
              {props.plants?.map((plant) => {
                return <DropdownOption key={plant.value} value={plant.value} label={plant.label} />;
              })}
            </Dropdown>
          </div>
        </AuthorizedComponent>
        <AuthorizedComponent
          accessRights={[AccessRights.SystemAdmin, AccessRights.ManageDeliveriesCompanies]}
        >
          <div>
            <Dropdown
              monochrome
              label={t("Country")}
              clearable
              searchable
              disabled={selectedCompany?.value === AllCompaniesSelectionIdentifier ? false : true}
              onChange={(
                selection: Array<{
                  value: any;
                  label: string;
                  isNew: boolean;
                }>
              ) => {
                handleFormValueChange(
                  props,
                  "countryCode",
                  selection.length > 0 ? selection[0].value : ""
                );
              }}
              value={[{ value: props.values.countryCode, label: props.values.countryCode }]}
            >
              {props.countries?.map((c) => {
                return <DropdownOption key={c.value} {...c} />;
              })}
            </Dropdown>
          </div>
        </AuthorizedComponent>
      </>
    );
  };

  return (
    <FormContainer>
      {!isLimitedSearch ? (
        <SearchFormInfo>
          <WildcardPopup />
        </SearchFormInfo>
      ) : null}

      <div>
        {isLimitedSearch ? null : (
          <InputLabel
            icon="abb/information-circle-1"
            label={t("Order / reference")}
            tooltipText={orderSearchTooltips.orderOrReference}
          />
        )}

        <Input
          type="normal"
          value={props.values.order}
          label={isLimitedSearch ? t("Purchase order") : undefined}
          showClearIcon
          dataType="text"
          onValueChange={(e: string) => {
            handleFormValueChange(props, "order", e);
          }}
          onKeyUp={(event) => handlePressEnter(event, onSubmit)}
        />
      </div>
      {isLimitedSearch ? drawSearchModeSwitch() : drawNormalSearchFields()}

      <HorizontalElementContainer>
        {!isLimitedSearch ? (
          <Button
            buttonType="secondary"
            text={t("Reset")}
            sizeClass="medium"
            disabled={!props.dirty}
            onClick={() => {
              props.resetForm();
            }}
          />
        ) : null}

        <Button
          buttonType="primary-black"
          text={t("Search")}
          sizeClass="medium"
          disabled={!props.isValid}
          onClick={() => {
            props.submitForm();
          }}
        />
      </HorizontalElementContainer>
    </FormContainer>
  );
};

export const OrderSearchForm = withFormik<OrderSearchFormProps, OrderSearchFields>({
  mapPropsToValues: () => {
    return orderSearchFormDefaultFields;
  },
  validationSchema: OrderSearchSchema,

  handleSubmit: (values, formikBag) => {
    formikBag.props.onSubmit(values, formikBag.props.selectedCompany?.value);
  }
})(InnerSearchForm);

export default OrderSearchForm;
